import { useState, useEffect } from 'react';
import { Mail, Loader2, Copy, Check, Phone, User, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { inviteService } from '@/services/invite.service';
import { weddingService } from '@/services/wedding.service';
import { toast } from 'sonner';

export const InviteMemberDialog = ({ open, onOpenChange, onInviteSent }) => {
  const { activeOrg, user, userRole } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('planner');
  const [selectedWeddings, setSelectedWeddings] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [loadingWeddings, setLoadingWeddings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Only org_admin can invite other org_admins
  const canInviteOrgAdmin = userRole === 'org_admin';
  const needsWeddingAssignment = role === 'planner' || role === 'client';
  
  // Load weddings when dialog opens and role requires assignment
  useEffect(() => {
    if (open && activeOrg?.id && needsWeddingAssignment) {
      loadWeddings();
    } else {
      setWeddings([]);
      setSelectedWeddings([]);
    }
  }, [open, activeOrg?.id, needsWeddingAssignment]);
  
  const loadWeddings = async () => {
    if (!activeOrg?.id) return;
    
    try {
      setLoadingWeddings(true);
      const data = await weddingService.getWeddings(activeOrg.id);
      setWeddings(data);
    } catch (error) {
      console.error('Failed to load weddings:', error);
      toast.error('Failed to load weddings');
    } finally {
      setLoadingWeddings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!phone.trim()) {
      toast.error('Phone number is required');
      setLoading(false);
      return;
    }
    
    // Validate wedding assignment for planner/client
    if (needsWeddingAssignment && selectedWeddings.length === 0) {
      toast.error('Please select at least one wedding for planner/client');
      setLoading(false);
      return;
    }

    try {
      const inviteData = {
        email,
        name,
        phone,
        role,
        wedding_ids: needsWeddingAssignment ? selectedWeddings : undefined
      };
      
      const invite = await inviteService.createInvite(activeOrg.id, inviteData);
      
      // The email is sent automatically by the backend, but we can still show the link for manual sharing
      const fullUrl = invite.invite_url || `${window.location.origin}/accept-invite?token=${invite.token || 'sent'}`;
      setInviteUrl(fullUrl);
      toast.success(`Invitation email sent to ${name} (${email})`);
      
      if (onInviteSent) {
        onInviteSent();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send invitation';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        toast.success('Invite link copied!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = inviteUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          textArea.remove();
          setCopied(true);
          toast.success('Invite link copied!');
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          textArea.remove();
          toast.error('Failed to copy. Please copy manually.');
        }
      }
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = inviteUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        setCopied(true);
        toast.success('Invite link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        textArea.remove();
        toast.error('Could not copy link. Please copy manually.');
      }
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setPhone('');
    setRole('planner');
    setSelectedWeddings([]);
    setWeddings([]);
    setInviteUrl('');
    setCopied(false);
    onOpenChange(false);
  };
  
  const handleWeddingToggle = (weddingId) => {
    if (role === 'client') {
      // Clients can only select one wedding - replace selection
      setSelectedWeddings(prev.includes(weddingId) ? [] : [weddingId]);
    } else {
      // Planners can select multiple weddings
      setSelectedWeddings(prev => 
        prev.includes(weddingId)
          ? prev.filter(id => id !== weddingId)
          : [...prev, weddingId]
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold gradient-text">
            Invite Team Member
          </DialogTitle>
        </DialogHeader>

        {!inviteUrl ? (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={setRole} disabled={loading}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {canInviteOrgAdmin && (
                      <SelectItem value="org_admin">
                        <div>
                          <div className="font-medium">Organization Admin</div>
                          <div className="text-xs text-muted-foreground">
                            Full access to organization and all weddings
                          </div>
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value="admin">
                      <div>
                        <div className="font-medium">Admin</div>
                        <div className="text-xs text-muted-foreground">
                          Full access to all weddings, manage team members
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="planner">
                      <div>
                        <div className="font-medium">Planner</div>
                        <div className="text-xs text-muted-foreground">
                          Full access to assigned weddings only
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="client">
                      <div>
                        <div className="font-medium">Client</div>
                        <div className="text-xs text-muted-foreground">
                          View-only access to assigned weddings
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Wedding Assignment for Planner/Client */}
              {needsWeddingAssignment && (
                <div>
                  <Label>
                    Assign to {role === 'client' ? 'Wedding' : 'Weddings'} *
                    {role === 'client' && (
                      <span className="text-xs text-muted-foreground ml-2">(Clients can only be assigned to one wedding)</span>
                    )}
                  </Label>
                  {loadingWeddings ? (
                    <div className="mt-1.5 p-4 text-center text-sm text-muted-foreground">
                      Loading weddings...
                    </div>
                  ) : weddings.length === 0 ? (
                    <Alert className="mt-1.5 border-warning/20 bg-warning/5">
                      <AlertDescription className="text-sm">
                        No weddings found. Please create a wedding first before inviting planners or clients.
                      </AlertDescription>
                    </Alert>
                  ) : role === 'client' ? (
                    // Client: Single selection dropdown
                    <Select
                      value={selectedWeddings[0] || ''}
                      onValueChange={(value) => setSelectedWeddings(value ? [value] : [])}
                      disabled={loading}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select a wedding" />
                      </SelectTrigger>
                      <SelectContent>
                        {weddings.map((wedding) => (
                          <SelectItem key={wedding.id} value={wedding.id}>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-medium">
                                {wedding.bride_name} & {wedding.groom_name}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({wedding.wedding_date})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    // Planner: Multiple selection checkboxes
                    <div className="mt-1.5 max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                      {weddings.map((wedding) => (
                        <div key={wedding.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`wedding-${wedding.id}`}
                            checked={selectedWeddings.includes(wedding.id)}
                            onCheckedChange={() => handleWeddingToggle(wedding.id)}
                            disabled={loading}
                          />
                          <Label
                            htmlFor={`wedding-${wedding.id}`}
                            className="flex-1 cursor-pointer text-sm font-normal"
                          >
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-medium">
                                {wedding.bride_name} & {wedding.groom_name}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({wedding.wedding_date})
                              </span>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedWeddings.length > 0 && role === 'planner' && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {selectedWeddings.length} wedding(s) selected
                    </p>
                  )}
                </div>
              )}

              <Alert className="border-primary/20 bg-primary/5">
                <AlertDescription className="text-sm">
                  They'll receive an invitation email with a link to join <strong>{activeOrg?.name}</strong>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" className="gradient-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4 py-4">
            <Alert className="border-success/20 bg-success/5">
              <AlertDescription className="text-sm">
                ✓ Invitation created! Share this link with {name} ({email})
              </AlertDescription>
            </Alert>

            <div>
              <Label>Invite Link</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={inviteUrl}
                  readOnly
                  className="font-mono text-sm"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <p className="font-medium">What happens next?</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• The invite link expires in 7 days</li>
                <li>• They can sign up or log in to accept</li>
                <li>• They'll join as a <strong className="text-foreground">{role.replace('_', ' ')}</strong></li>
              </ul>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="gradient-primary w-full">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteMemberDialog;
